import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react'
import { CaseSensitive, Check, FileQuestion, HardDrive, Info, Library, Pencil, Search, Trash2, Upload, X } from 'lucide-react'
import { cgFontsApi } from '../../lib/api.js'
import type { CgFont } from '../../types/index.js'
import { Spinner } from '../common/Spinner.js'

interface Props {
  active?: boolean
  fontsVersion: number
  onFontsChanged: () => void
}

interface FontTileProps {
  font: CgFont
  deleting: boolean
  renaming: boolean
  onDelete: (font: CgFont) => void
  onRename: (font: CgFont, name: string) => void
}

const MAX_FONT_SIZE = 5 * 1024 * 1024
const FONT_EXTENSIONS = ['woff2', 'ttf', 'otf']

function isSupportedFont(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase()
  // Les polices se reconnaissent a l'extension : le type MIME envoye varie.
  return Boolean(extension && FONT_EXTENSIONS.includes(extension))
}

function formatFontSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`
}

const FontTile = memo(function FontTile({ font, deleting, renaming, onDelete, onRename }: FontTileProps) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(font.name)
  const inputRef = useRef<HTMLInputElement>(null)

  const deleteFont = useCallback(() => onDelete(font), [font, onDelete])
  const startEdit = useCallback(() => { setValue(font.name); setEditing(true) }, [font.name])
  const cancelEdit = useCallback(() => setEditing(false), [])
  const submitEdit = useCallback(() => {
    const next = value.trim()
    if (!next || next === font.name) { setEditing(false); return }
    onRename(font, next)
    setEditing(false)
  }, [font, onRename, value])
  const onInputKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') { event.preventDefault(); submitEdit() }
    else if (event.key === 'Escape') { event.preventDefault(); cancelEdit() }
  }, [cancelEdit, submitEdit])

  useEffect(() => {
    if (editing) { inputRef.current?.focus(); inputRef.current?.select() }
  }, [editing])

  return (
    <article className={`cg-library-asset${font.missing ? ' is-missing' : ''}`}>
      {/* La police est chargee via @font-face pour afficher son specimen dans la
          vignette, exactement comme a l'antenne. */}
      {!font.missing && (
        <style>{`@font-face { font-family: '${font.fontFamily}'; src: url('${font.url}'); font-display: block; }`}</style>
      )}
      <div className="cg-library-asset-preview">
        {font.missing
          ? <FileQuestion size={22} aria-hidden="true" />
          : <span className="cg-library-asset-specimen" style={{ fontFamily: font.fontFamily }}>Ag</span>}
        <span className="cg-library-asset-kind"><CaseSensitive size={11} />POLICE</span>
      </div>
      <div className="cg-library-asset-body">
        {editing ? (
          <input
            ref={inputRef}
            className="cg-library-asset-rename-input"
            value={value}
            disabled={renaming}
            onChange={event => setValue(event.target.value)}
            onKeyDown={onInputKeyDown}
            aria-label="Nouveau nom de la police"
          />
        ) : (
          <strong title={font.name}>{font.name}</strong>
        )}
        <span>{font.missing ? 'Fichier introuvable' : formatFontSize(font.sizeBytes)}</span>
      </div>
      {editing ? (
        <>
          <button type="button" className="cg-icon-button is-cancel" onClick={cancelEdit} disabled={renaming} title="Annuler" aria-label="Annuler le renommage"><X size={13} /></button>
          <button type="button" className="cg-icon-button is-primary is-confirm" onClick={submitEdit} disabled={renaming || !value.trim()} title="Valider le nouveau nom" aria-label="Valider le nouveau nom"><Check size={13} /></button>
        </>
      ) : (
        <>
          <button type="button" className="cg-icon-button is-rename" onClick={startEdit} disabled={renaming} title="Renommer" aria-label={`Renommer ${font.name}`}><Pencil size={13} /></button>
          <button
            type="button"
            className="cg-icon-button is-danger"
            onClick={deleteFont}
            disabled={deleting}
            title="Supprimer definitivement"
            aria-label={`Supprimer ${font.name}`}
          ><Trash2 size={13} /></button>
        </>
      )}
    </article>
  )
})

export const CgFontsLibrary = memo(function CgFontsLibrary({ active = true, fontsVersion, onFontsChanged }: Props) {
  const [fonts, setFonts] = useState<CgFont[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const loadFonts = useCallback(async () => {
    setLoading(true)
    try {
      setFonts(await cgFontsApi.list())
    } catch (error: any) {
      setMessage(error.message ?? 'Chargement impossible')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { if (active) void loadFonts() }, [active, fontsVersion, loadFonts])

  const openImport = useCallback(() => inputRef.current?.click(), [])

  const importFont = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!isSupportedFont(file)) {
      setMessage('Formats acceptes : WOFF2, TTF et OTF')
      return
    }
    if (file.size > MAX_FONT_SIZE) {
      setMessage('Police trop lourde (5 Mo maximum)')
      return
    }
    setUploading(true)
    setMessage('')
    try {
      const font = await cgFontsApi.upload(file)
      setFonts(current => [font, ...current])
      onFontsChanged()
      setMessage('Police importee')
    } catch (error: any) {
      setMessage(error.message ?? 'Import impossible')
    } finally {
      setUploading(false)
    }
  }, [onFontsChanged])

  const deleteFont = useCallback(async (font: CgFont) => {
    if (!window.confirm(`Supprimer definitivement « ${font.name} » ? Les habillages qui l'utilisent reviendront a la police par defaut.`)) return
    setDeletingId(font.id)
    setMessage('')
    try {
      await cgFontsApi.delete(font.id)
      setFonts(current => current.filter(item => item.id !== font.id))
      onFontsChanged()
      setMessage('Police supprimee definitivement')
    } catch (error: any) {
      setMessage(error.message ?? 'Suppression impossible')
      await loadFonts()
    } finally {
      setDeletingId(null)
    }
  }, [loadFonts, onFontsChanged])

  const renameFont = useCallback(async (font: CgFont, name: string) => {
    setRenamingId(font.id)
    setMessage('')
    try {
      const updated = await cgFontsApi.rename(font.id, name)
      setFonts(current => current.map(item => item.id === updated.id ? updated : item))
      onFontsChanged()
      setMessage('Police renommee')
    } catch (error: any) {
      setMessage(error.message ?? 'Renommage impossible')
    } finally {
      setRenamingId(null)
    }
  }, [onFontsChanged])

  const filteredFonts = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('fr-FR')
    return normalized ? fonts.filter(font => font.name.toLocaleLowerCase('fr-FR').includes(normalized)) : fonts
  }, [fonts, query])
  const totalSize = useMemo(() => fonts.reduce((sum, font) => sum + font.sizeBytes, 0), [fonts])

  return (
    <section className="cg-assets-library">
      <header className="cg-assets-toolbar">
        <div className="cg-assets-summary">
          <Library size={16} />
          <strong>Bibliotheque de polices</strong>
          <span>{fonts.length} police{fonts.length > 1 ? 's' : ''}</span>
          <span><HardDrive size={12} />{formatFontSize(totalSize)}</span>
        </div>
        <label className="cg-assets-search">
          <Search size={14} aria-hidden="true" />
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Rechercher" aria-label="Rechercher une police" />
        </label>
        <button type="button" className="cg-button is-primary" onClick={openImport} disabled={uploading}>
          <Upload size={14} />{uploading ? 'Traitement...' : 'Importer'}
        </button>
        {message && <span className="cg-inline-message"><Info size={13} />{message}</span>}
      </header>

      <div className="cg-assets-content">
        {active && filteredFonts.map(font => <FontTile key={font.id} font={font} deleting={deletingId === font.id} renaming={renamingId === font.id} onDelete={deleteFont} onRename={renameFont} />)}
        {!loading && filteredFonts.length === 0 && (
          <div className="cg-assets-empty"><Library size={24} /><span>{query ? 'Aucun resultat' : 'Aucune police'}</span></div>
        )}
        {loading && <div className="cg-assets-empty"><Spinner /> <span>Chargement...</span></div>}
      </div>
      <p className="cg-settings-hint">Les polices sont partagees par tous les habillages et logos, toutes chaines confondues.</p>
      <input ref={inputRef} className="cg-file-input" type="file" accept="font/woff2,font/ttf,font/otf,.woff2,.ttf,.otf" onChange={importFont} />
    </section>
  )
})
